import { useMemo, useState } from 'react';
import { usePageTitle } from '../../../hooks/usePageTitle';
import { STRIPE_LINKS } from '../../../lib/stripe';
import CalculatorLayout from '../../../components/calculator/CalculatorLayout';
import NumberInput from '../../../components/calculator/NumberInput';
import PercentInput from '../../../components/calculator/PercentInput';
import BigResult from '../../../components/calculator/BigResult';
import EmailCapture from '../../../components/calculator/EmailCapture';
import CtaBlock from '../../../components/calculator/CtaBlock';
import FaqSection from '../../../components/calculator/FaqSection';
import RelatedCalculators from '../../../components/calculator/RelatedCalculators';

const SLUG = 'seo-traffic-value';
const CANONICAL = `https://floridaaimarketingexperts.com/calculator/${SLUG}`;

export default function SeoTrafficValueCalculatorPage() {
  const softwareSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Local SEO Traffic Value Calculator',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Any (Web)',
    url: CANONICAL,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    description:
      'Free local SEO ROI calculator. Turn organic visitors into projected monthly revenue at 2x and 5x traffic levels.',
  };
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'How do I calculate SEO ROI for a local business?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Multiply your current monthly organic visitors by your visitor-to-lead conversion rate to get leads, multiply by close rate to get deals, then multiply by average deal value to get revenue. Our calculator does this and projects 2x and 5x traffic.',
        },
      },
      {
        '@type': 'Question',
        name: 'What is a good conversion rate for a Florida business website?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'For service businesses, 3-8% visitor-to-lead conversion is typical. With click-to-call, a clear service list, and review proof, 8%+ is achievable. If you are under 2%, something is broken on the site.',
        },
      },
      {
        '@type': 'Question',
        name: 'How long does it take to double local SEO traffic?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'For most Florida businesses with 500-2,000 organic visitors/month, doubling takes 4-7 months of consistent work: Google Business Profile optimization, local citations, review velocity, schema, and 2-4 service+city pages per month.',
        },
      },
    ],
  };

  usePageTitle({
    title: 'Local SEO Traffic Value Calculator | FAME',
    description:
      'Free local SEO ROI calculator. Turn your current organic traffic into projected monthly revenue, and see what 2x or 5x traffic is worth.',
    keywords:
      'local SEO ROI calculator, seo traffic value calculator, organic traffic revenue calculator, Florida SEO ROI',
    canonical: CANONICAL,
    schema: [softwareSchema, faqSchema],
  });

  const [monthlyVisitors, setMonthlyVisitors] = useState(1200);
  const [conversionRate, setConversionRate] = useState(4);
  const [avgJobValue, setAvgJobValue] = useState(2800);
  const [closeRate, setCloseRate] = useState(35);

  const results = useMemo(() => {
    const leadRate = conversionRate / 100;
    const closeRatePct = closeRate / 100;
    const monthlyLeads = monthlyVisitors * leadRate;
    const monthlyJobs = monthlyLeads * closeRatePct;
    const monthlyRevenue = monthlyJobs * avgJobValue;
    const at2x = monthlyRevenue * 2;
    const at5x = monthlyRevenue * 5;
    const annual = monthlyRevenue * 12;
    const annualAt5x = at5x * 12;
    return { monthlyLeads, monthlyJobs, monthlyRevenue, at2x, at5x, annual, annualAt5x };
  }, [monthlyVisitors, conversionRate, avgJobValue, closeRate]);

  const faqs = [
    {
      question: 'Where do I find my current organic visitor count?',
      answer:
        'Google Search Console (free) shows organic clicks per month. Google Analytics 4 shows organic sessions under Acquisition > Traffic acquisition. If you do not have either set up, we do that as step one of any retainer.',
    },
    {
      question: 'Is this calculator realistic for small markets?',
      answer:
        'Yes. The math is the same whether you serve Miami or a small Florida town — it scales linearly. A town of 50K might have 300 monthly searches instead of 3,000. Per-visitor revenue value is often higher in smaller markets because competition is lower.',
    },
    {
      question: 'Why does 5x traffic drive 5x revenue here?',
      answer:
        'The calculator holds conversion and close rate flat, so revenue scales proportionally with traffic. In practice, your first doubling is easiest — you are capturing obvious keywords. Higher multiples take compounding work (content depth, topical authority, review velocity, GEO citations).',
    },
  ];

  return (
    <CalculatorLayout>
      <section className="relative overflow-hidden bg-white grid-bg py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <span className="eyebrow mb-5">
            <i className="ri-search-line mr-2"></i>Free Calculator · Local SEO Math
          </span>
          <h1 className="h-display text-4xl md:text-6xl mb-5 leading-[0.95] tracking-[-0.02em]">
            Local SEO Traffic Value Calculator
          </h1>
          <p className="text-lg md:text-xl text-slate-600 font-body font-light max-w-3xl mx-auto">
            Turn your organic traffic into projected revenue. See what your current SEO is worth —
            and what you are leaving on the table at 2x and 5x traffic.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div className="fame-card p-8 shadow-xl">
              <h2 className="text-navy-900 text-2xl font-display font-bold mb-6">Your Numbers</h2>
              <div className="flex flex-col gap-6">
                <NumberInput
                  label="Current monthly organic visitors"
                  value={monthlyVisitors}
                  onChange={setMonthlyVisitors}
                  step={50}
                  helperText="From Google Search Console or GA4."
                />
                <PercentInput
                  label="Visitor-to-lead conversion rate"
                  value={conversionRate}
                  onChange={setConversionRate}
                  step={0.5}
                  helperText="Industry average: 3-8%."
                />
                <NumberInput
                  label="Average deal value"
                  value={avgJobValue}
                  onChange={setAvgJobValue}
                  prefix="$"
                  step={100}
                />
                <PercentInput
                  label="Close rate on leads"
                  value={closeRate}
                  onChange={setCloseRate}
                  helperText="Percent of web leads you close."
                />
              </div>
            </div>

            <div className="flex flex-col gap-5">
              <BigResult
                label="Current Monthly SEO Revenue"
                value={results.monthlyRevenue}
                format="currency"
                accent="teal"
                sublabel={`${Math.round(results.monthlyLeads)} leads → ${Math.round(
                  results.monthlyJobs
                )} booked deals a month.`}
              />
              <div className="grid grid-cols-2 gap-4">
                <BigResult
                  label="At 2× Traffic"
                  value={results.at2x}
                  format="currency"
                  accent="orange"
                  size="md"
                />
                <BigResult
                  label="At 5× Traffic"
                  value={results.at5x}
                  format="currency"
                  accent="green"
                  size="md"
                />
              </div>
              <BigResult
                label="Projected Annual Revenue at 5× Traffic"
                value={results.annualAt5x}
                format="currency"
                accent="navy"
                size="md"
              />

              <CtaBlock
                variant="compact"
                eyebrow="Compound your SEO"
                headline="Managed Growth · $497/mo · or Growth Retainer · $5,000/mo"
                subheadline="Local SEO, schema, content engine, review velocity — done for you."
                primaryLabel="Managed Growth"
                primaryHref={STRIPE_LINKS.T6_MANAGED_GROWTH}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-navy-50">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-navy-900 text-2xl md:text-3xl font-display font-bold mb-4">
            What organic traffic is actually worth to a Florida business
          </h2>
          <p className="text-navy-600 text-base md:text-lg leading-relaxed mb-4">
            Organic search is the only marketing channel that compounds. Paid ads stop the second
            you stop paying. Referrals depend on one person. But a page ranking #1 in Google for
            &quot;[your service] [your city]&quot; keeps generating leads every month for years at
            marginal cost zero. The question is how much that traffic is actually worth.
          </p>
          <p className="text-navy-600 text-base md:text-lg leading-relaxed mb-4">
            The math is simple and ruthless:{' '}
            <strong>visitors × conversion × close × deal value</strong>. A Florida business with
            1,200 monthly visitors, a 4% conversion rate, 35% close, and a $2,800 average deal is
            producing <strong>$47K/month</strong> from the website. Most owners do not realize it
            because the dollars arrive over time — never in a single line item on a P&amp;L.
          </p>
          <p className="text-navy-600 text-base md:text-lg leading-relaxed">
            Now run the 5x number. That is what is sitting on the table. A 5x improvement in
            organic + generative search is realistic over 12-18 months with a FAME Growth Retainer,
            and it is typically the single highest-leverage marketing investment you can make.
          </p>
        </div>
      </section>

      <FaqSection items={faqs} />

      <CtaBlock
        eyebrow="Built for Florida brands"
        headline="Compound your SEO with Managed Growth or a T10 Retainer"
        subheadline="Managed Growth ($497/mo) covers SEO fundamentals. T10 Growth ($5,000/mo) adds paid, content, and attribution as one team."
        primaryLabel="Managed Growth · $497/mo"
        primaryHref={STRIPE_LINKS.T6_MANAGED_GROWTH}
        secondaryLabel="Growth Retainer · $5,000/mo"
        secondaryHref={STRIPE_LINKS.T10_GROWTH}
      />

      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <EmailCapture
            slug={SLUG}
            calculatorInputs={{ monthlyVisitors, conversionRate, avgJobValue, closeRate }}
            calculatorResults={{
              monthlyRevenue: Math.round(results.monthlyRevenue),
              at2x: Math.round(results.at2x),
              at5x: Math.round(results.at5x),
              annualAt5x: Math.round(results.annualAt5x),
            }}
          />
        </div>
      </section>

      <RelatedCalculators excludeSlug={SLUG} />
    </CalculatorLayout>
  );
}
